import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import { getData } from "../../../services/library";
import { ExportToCsv } from 'export-to-csv';
// Componente memorizado para evitar re-renders innecesarios
const EmpleadoListItem = React.memo(({ empleado }) => (
  <ListItem>
    <ListItemText
      primary={empleado.nombre}
      secondary="Talle: no registrado"
    />
  </ListItem>
));

function ConfirmacionModal({ empleadosSinStock, open, onCancel, onContinue }) {
  const [tallesEspecificos, setTallesEspecificos] = useState([]);
  
  // 1. Cargar talles solo cuando el modal se abre por primera vez
  useEffect(() => {
    if (open && tallesEspecificos.length === 0) {
      getData("/stock_prendas_talles", setTallesEspecificos);
    }
  }, [open]);

  // 2. Memoizar el mapeo de talles
  const mapeoTalles = useMemo(() => (
    tallesEspecificos.reduce((acc, talle) => {
      acc[talle.id] = talle.talle;
      return acc;
    }, {})
  ), [tallesEspecificos]);

  // 3. Memorizar el filtrado de empleados
  const empleadosFiltrados = useMemo(() => {
    const resultado = {};
    
    Object.entries(empleadosSinStock).forEach(([articulo, empleados]) => {
      const sinTalle = empleados.filter(emp => !mapeoTalles[emp.talle] && emp.xtalle === 1);
      if (sinTalle.length > 0) {
        resultado[articulo] = sinTalle;
      }
    });
    
    return resultado;
  }, [empleadosSinStock, mapeoTalles]);

  // 4. Contar empleados filtrados para optimizar
  const totalEmpleadosFiltrados = useMemo(() => (
    Object.values(empleadosFiltrados).reduce((total, emp) => total + emp.length, 0)
  ), [empleadosFiltrados]);


  const handleExportCSV = () => {
    // Preparar los datos para exportar
    const dataToExport = [];
    
    Object.entries(empleadosFiltrados).forEach(([articulo, empleados]) => {
      empleados.forEach(empleado => {
        dataToExport.push({
          'Artículo': articulo,
          'Nombre Empleado': empleado.nombre,
          'Legajo': empleado.nro_legajo || 'N/A',
          'Departamento': empleado.departamento || 'N/A',
          'Talle Registrado': 'No',
          'Fecha': new Date().toLocaleDateString()
        });
      });
    });

    // Configuración del CSV
    const options = { 
      filename: `empleados_sin_talles_${new Date().toLocaleDateString()}`,
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalSeparator: ',',
      showLabels: true, 
      showTitle: true,
      title: 'Empleados con talles no registrados',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(dataToExport);
  };

  // 5. Evitar render si no hay empleados
  if (totalEmpleadosFiltrados === 0) {
    return null;
  }



  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Empleados con talle no registrado</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Atención:</strong> los empleados listados a continuación no tienen un talle registrado y la ficha no se generará.
        </Alert>
        <Alert severity="info" sx={{ mb: 3 }}>
          Si el stock actual es menor a la cantidad entregada, el stock quedará en negativo. Deberás corregirlo manualmente.
        </Alert>

        {Object.entries(empleadosFiltrados).map(([articulo, empleados]) => (
          <div key={articulo}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2 }}>
              {articulo}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Empleados sin talle registrado: {empleados.length}
            </Typography>
            <List dense>
              {empleados.map((empleado, index) => (
                <EmpleadoListItem key={index} empleado={empleado} />
              ))}
            </List>
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button 
            onClick={handleExportCSV} 
            color="primary"
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Exportar a CSV
          </Button>
        </Box>
        <Button  onClick={onCancel} variant="contained" color="error">
          Cancelar
        </Button>
        <Button onClick={onContinue} color="success" variant="contained">
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Exportar el componente memoizado
export default React.memo(ConfirmacionModal);